import * as RadixSelect from '@radix-ui/react-select'
import React from 'react'
import { ChevronDown } from '../icons'

export function Select({
  className,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
}: {
  className?: string
  placeholder?: string
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const hasValidValue = options.some((opt) => opt.value === value)
  const displayValue = hasValidValue ? value : undefined

  return (
    <RadixSelect.Root value={displayValue} onValueChange={onChange} onOpenChange={setIsOpen}>
      <RadixSelect.Trigger
        className={`w-full flex items-center justify-between gap-4 px-4 py-3 bg-white border border-borderGray text-secondaryText rounded-md shadow-soft focus:outline-none ${className}`}
      >
        <span className="text-base text-left overflow-hidden text-ellipsis whitespace-nowrap flex-1">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon className="flex-shrink-0">
          <ChevronDown rotated={!isOpen} className="transition-transform duration-200" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={5}
          className="bg-white border border-borderGray rounded-sm shadow-soft z-50 w-[var(--radix-select-trigger-width)] max-h-[300px] overflow-hidden"
        >
          <style>{`
            [data-radix-select-viewport]::-webkit-scrollbar {
              width: 8px;
            }
            [data-radix-select-viewport]::-webkit-scrollbar-track {
              background: #f3f4f6;
              border-radius: 4px;
            }
            [data-radix-select-viewport]::-webkit-scrollbar-thumb {
              background: #d1d5db;
              border-radius: 4px;
            }
            [data-radix-select-viewport]::-webkit-scrollbar-thumb:hover {
              background: #9ca3af;
            }
          `}</style>
          <RadixSelect.Viewport
            className="p-1 max-h-[300px] overflow-y-scroll"
            style={
              {
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db #f3f4f6',
              } as React.CSSProperties
            }
          >
            {options.length === 0 ? (
              <div className="px-4 py-2 text-secondaryText text-sm">No options available</div>
            ) : (
              options.map((option) => {
                return (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                )
              })
            )}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}

const SelectItem = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof RadixSelect.Item>>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <RadixSelect.Item
        className={`px-4 py-2 cursor-pointer hover:bg-blueLight focus:bg-blueLight outline-none rounded ${className}`}
        {...props}
        ref={forwardedRef}
      >
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      </RadixSelect.Item>
    )
  },
)

SelectItem.displayName = 'SelectItem'
